import { makeAutoObservable } from 'mobx'
import { logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import { ComplianceApi, type IIrsEinDocument } from 'entities/Compliance'
import { Attachment } from 'entities/Attachment/model/Attachment'
import type { IResponseMedia } from 'entities/Attachment'

export class IRSEINDocumentUploaderStore {
  private _irsEinAttachment: IIrsEinDocument | null = null
  private _attachmentError: {
    title?: string
    desc?: string
  } | null = null
  private _attachmentErrorTimeout: number | null = null
  private _filePreview: File | null = null

  constructor() {
    makeAutoObservable(this)
  }

  get irsEinAttachment() {
    return this._irsEinAttachment
  }

  get attachmentData() {
    if (this._filePreview) return new Attachment({ file: this._filePreview, loading: true })
    if (!this._irsEinAttachment) return null

    return new Attachment({
      responseMedia: {
        id: this._irsEinAttachment.id,
        size: this._irsEinAttachment.size,
        source: this._irsEinAttachment.source_url,
        name: this._irsEinAttachment.title,
        content_type: this._irsEinAttachment.extension,
      } as IResponseMedia,
    })
  }

  get attachmentError() {
    return this._attachmentError
  }

  setIrsEinAttachment = (data: IIrsEinDocument | null) => {
    this._irsEinAttachment = data
  }

  deleteIrsEinAttachment = async () => {
    try {
      await ComplianceApi.deleteBpBusinessInformationEinDocument()

      this.setIrsEinAttachment(null)
    } catch (error) {
      logger.error(error)
    }
  }

  setAttachmentError = (error: { title?: string; desc?: string } | null) => {
    this.clearAttachmentError()
    this._attachmentError = error

    if (error === null) return

    this._attachmentErrorTimeout = window.setTimeout(() => {
      this._attachmentError = null
    }, 5000)
  }

  clearAttachmentError = () => {
    this._attachmentError = null

    if (this._attachmentErrorTimeout) {
      clearTimeout(this._attachmentErrorTimeout)
    }
  }

  reset = () => {
    this.clearAttachmentError()
    this._irsEinAttachment = null
    this._filePreview = null
    this._attachmentErrorTimeout = null
  }

  uploadIrsEinAttachment = async (file: File | null) => {
    if (!file) return

    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg']
    const sizeLimits = {
      'image/png': 10 * 1024 * 1024,
      'image/jpeg': 10 * 1024 * 1024,
      'application/pdf': 10 * 1024 * 1024,
    }

    if (!allowedTypes.includes(file.type)) {
      this.setAttachmentError({
        title: 'The file is not supported for upload',
        desc: 'Please upload only: pdf, jpg, png',
      })
      return
    }

    const maxSize = sizeLimits[file.type as keyof typeof sizeLimits]
    if (file.size > maxSize) {
      this.setAttachmentError({
        desc: file.type.includes('image')
          ? 'The image file is too large. It must not exceed 10 MB'
          : 'The file is too large. It must not exceed 10 MB',
      })
      return
    }

    if (this._irsEinAttachment) {
      await this.deleteIrsEinAttachment()
      this.setIrsEinAttachment(null)
    }

    this._filePreview = file

    try {
      const { data } = await ComplianceApi.setBpBusinessInformationEinDocument(file)

      this.setIrsEinAttachment(data)

      toastStore.add({
        title: '1 file uploaded',
        type: 'success',
      })
    } catch (error) {
      logger.error(error)
    } finally {
      this._filePreview = null
    }
  }
}
